import type { ReactNode } from "react";

import { Field } from "./Field";

export interface SelectOption<T extends string> {
  value: T;
  label: string;
}

export interface SelectProps<T extends string> {
  label: string;
  description?: string;
  value: T;
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
}

/**
 * A native `<select>`. Every settings choice in the specs is a short closed
 * list, and the native control is keyboard- and mobile-correct for free.
 */
export function Select<T extends string>({
  label,
  description,
  value,
  options,
  onChange,
  disabled = false,
}: SelectProps<T>): ReactNode {
  return (
    <Field label={label} {...(description === undefined ? {} : { description })}>
      {(id) => (
        <select
          id={id}
          value={value}
          disabled={disabled}
          onChange={(event) => {
            onChange(event.target.value as T);
          }}
          className="w-full rounded-md border border-line bg-raised px-3 py-2 text-sm disabled:opacity-50"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}
